import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { Project, TreeItem, ProjectContextType } from '../types';
import {
  projectsApi,
  treeItemsApi,
  foldersApi,
  usersApi,
  mapApiProjectToProject,
  ApiError,
  type CreateProjectParams,
} from '../services/api';
import { OpenFeature } from '@openfeature/web-sdk';
import { useAuth } from './AuthContext';
import { usePagesIndexStore } from '../stores/pagesIndexStore';
import { useAgentsStore } from '../stores/agentsStore';
import { conversationItem, fileItem, locateItem, useAgentTreeStore } from '../stores/agentTreeStore';
import { useUserPreferencesStore } from '../hooks/useUserPreferences';
import { ValuePathProvider } from '../lib/openfeature-provider';

// Extended context type with CRUD operations
interface ExtendedProjectContextType extends ProjectContextType {
  createProject: (params: CreateProjectParams) => Promise<Project>;
  updateProject: (projectId: string, params: Partial<CreateProjectParams>) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  /** The first load again, and every agent's files and conversations already
   *  loaded — after a change the screen did not make itself. */
  refreshProjects: () => Promise<void>;
  // Folder/Item CRUD operations, on the agent's tree that holds the item
  // (stores/agentTreeStore.ts) — a tree never spans agents.
  /** A folder in `agentId`'s tree — under `parentId`, which must be that agent's. */
  createFolder: (name: string, agentId: string, parentId?: string | null) => Promise<TreeItem>;
  moveItem: (itemId: string, newParentId: string | null) => Promise<void>;
  renameItem: (itemId: string, newName: string) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  /** A row the server has not listed yet, into its agent's tree (an upload,
   *  an import) or its conversations. Ignored for an agent not loaded: its
   *  first read will carry the row. */
  addOptimisticItem: (item: TreeItem) => void;
  /** The active project's recent work (`projectsApi.recent`), flat: Home's rows. */
  recentItems: TreeItem[];
  /** Ask for the active project's recent work again. */
  refreshRecent: () => Promise<void>;
  error: string | null;
}

const ProjectContext = createContext<ExtendedProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  children: ReactNode;
}

/** Remove `itemId` wherever it sits in a tree. */
function removeFromTree(items: TreeItem[], itemId: string): TreeItem[] {
  return items
    .filter((a) => a.id !== itemId)
    .map((a) => (a.children ? { ...a, children: removeFromTree(a.children, itemId) } : a));
}

/** `toAdd` under `parentId` (a folder of this tree), or at the root. `prepend`
 *  puts it first — the newest-first order of the files listing. */
function addToTree(items: TreeItem[], toAdd: TreeItem, parentId: string | null | undefined, prepend = false): TreeItem[] {
  const node = { ...toAdd, parentId: parentId ?? null, children: toAdd.children ?? [] };
  if (!parentId) {
    if (items.some((i) => i.id === toAdd.id)) return items;
    return prepend ? [node, ...items] : [...items, node];
  }
  return items.map((a) => {
    if (a.id === parentId && a.type === 'folder') {
      const kids = a.children ?? [];
      if (kids.some((c) => c.id === toAdd.id)) return a;
      return { ...a, children: prepend ? [node, ...kids] : [...kids, node] };
    }
    return a.children ? { ...a, children: addToTree(a.children, toAdd, parentId, prepend) } : a;
  });
}

/** Rename `itemId` wherever it sits. */
function renameInTree(items: TreeItem[], itemId: string, name: string): TreeItem[] {
  return items.map((a) => {
    if (a.id === itemId) return { ...a, name };
    return a.children ? { ...a, children: renameInTree(a.children, itemId, name) } : a;
  });
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // First load versus every refresh after it — see fetchProjects.
  const hasLoadedOnce = useRef(false);
  const [recent, setRecent] = useState<{ projectId: string; items: TreeItem[] } | null>(null);

  // The project's recent work — Home's rows. There is no project tree: each
  // agent's files and conversations load when that agent is needed
  // (stores/agentTreeStore.ts). Losing these costs Home its rows, never the screen.
  const loadRecent = useCallback(async (projectId: string): Promise<void> => {
    try {
      const rows = await projectsApi.recent(projectId);
      setRecent({ projectId, items: [...rows.files.map(fileItem), ...rows.conversations.map(conversationItem)] });
    } catch (err) {
      console.error('Failed to load recent work:', err);
    }
  }, []);

  // The signed-in user's first load, then the shown project's recent work.
  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setProjects([]);
      setActiveProject(null);
      setRecent(null);
      useAgentTreeStore.getState().reset();
      hasLoadedOnce.current = false;
      setIsLoading(false);
      return;
    }

    // `isLoading` means *nothing to show yet*, not *a request is in flight*.
    // Every refresh is also a re-fetch of the whole first load, and consumers
    // render nothing while it is true — so flipping it on a refresh unmounts
    // and remounts the list, silently discarding local state. Adding a member
    // collapsed the very card you added them from.
    const refreshing = hasLoadedOnce.current;
    if (!refreshing) setIsLoading(true);
    setError(null);

    try {
      // One request for everything the user reaches, preferences included
      const { projects: apiProjects, agents, userPreferences, flags } = await usersApi.start();

      // Before the projects land: the first frame drawn knows whose every pin
      // is. Asked for per project after this, each answer took a pin of
      // another project's agent off the sidebar as it came in.
      if (agents) useAgentsStore.getState().prime(agents);

      // Set up OpenFeature with the flags the first load resolved
      OpenFeature.setProvider(new ValuePathProvider(flags ?? {}));

      // Shared projects arrive here with no endpoint change — the first load
      // returns whatever RLS returns, and it returns reach rather than
      // ownership. Each carries the acting user's role and its creator.
      const loaded: Project[] = apiProjects.map(mapApiProjectToProject);
      // An empty list is a state, never repaired here: nothing makes a project
      // for anyone (docs/projects/index.md § What a project is), and AppFrame
      // shows the first run. Creating one on an empty list was a
      // check-then-act across the network — two overlapping inits both saw
      // zero and made two.
      setProjects(loaded);

      // Set preferences directly from init response (no extra API call)
      useUserPreferencesStore.getState().setPreferences(userPreferences);
      useUserPreferencesStore.setState({ isInitialized: true });

      // Set active project - prefer saved ID from preferences, then current, then first
      let shown: Project | null = null;
      if (loaded.length > 0 && !activeProject) {
        const savedProjectId = useUserPreferencesStore.getState().preferences.activeProjectId;
        const savedProject = savedProjectId ? loaded.find((p) => p.id === savedProjectId) : null;
        shown = savedProject || loaded[0];
        // Persist activeProjectId so TabProvider can bind tabs to the project
        if (!savedProjectId) {
          useUserPreferencesStore.getState().updatePreferences({ activeProjectId: shown.id });
        }
      } else if (activeProject) {
        // Update the active project with fresh data; if it was deleted, the first
        shown = loaded.find((p) => p.id === activeProject.id) ?? loaded[0] ?? null;
      }
      setActiveProject(shown);

      if (shown) void loadRecent(shown.id);
      else setRecent(null);
      // A refresh follows a change the screen did not make — a new
      // conversation, a delete elsewhere — so what is loaded is read again.
      if (refreshing) void useAgentTreeStore.getState().refreshLoaded();
    } catch (err) {
      console.error('Failed to load projects:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Your projects could not be loaded');
      }
      // A failed refresh keeps what the last load showed: a blip must not
      // empty the sidebar or unmount the screen. A failed first load has
      // nothing to keep; the error carries a retry (`refreshProjects`).
    } finally {
      hasLoadedOnce.current = true;
      setIsLoading(false);
    }
  }, [isAuthenticated, user, activeProject?.id, loadRecent]);

  // Fetch projects when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchProjects();
    }
  }, [authLoading, isAuthenticated, user?.uid]);

  const handleSetActiveProject = async (project: Project | null) => {
    if (!project) {
      setActiveProject(null);
      useUserPreferencesStore.getState().updatePreferences({ activeProjectId: undefined });
      return;
    }
    setActiveProject(project);
    useUserPreferencesStore.getState().updatePreferences({ activeProjectId: project.id });
    if (recent?.projectId !== project.id) void loadRecent(project.id);
  };

  const refreshRecent = useCallback(async (): Promise<void> => {
    if (activeProject) await loadRecent(activeProject.id);
  }, [activeProject?.id, loadRecent]);

  const createProject = useCallback(async (params: CreateProjectParams): Promise<Project> => {
    try {
      const newProject = await projectsApi.create(params);
      setProjects((prev) => [newProject, ...prev]);

      // If no active project, set the new one as active
      if (!activeProject) {
        setActiveProject(newProject);
      }

      return newProject;
    } catch (err) {
      console.error('Failed to create agent:', err);
      throw err;
    }
  }, [activeProject]);

  const updateProject = useCallback(async (
    projectId: string,
    params: Partial<CreateProjectParams>
  ): Promise<Project> => {
    try {
      const apiProject = await projectsApi.update(projectId, params);

      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, name: apiProject.name, description: apiProject.description ?? '' } : p))
      );

      if (activeProject?.id === projectId) {
        setActiveProject((prev) => prev ? { ...prev, name: apiProject.name, description: apiProject.description ?? '' } : prev);
      }

      return activeProject!;
    } catch (err) {
      console.error('Failed to update project:', err);
      throw err;
    }
  }, [activeProject?.id]);

  const deleteProject = useCallback(async (projectId: string): Promise<void> => {
    try {
      await projectsApi.delete(projectId);

      setProjects((prev) => {
        const remaining = prev.filter((p) => p.id !== projectId);

        // If we deleted the active project, select another one
        if (activeProject?.id === projectId) {
          if (remaining.length > 0) {
            setActiveProject(remaining[0]);
          } else {
            setActiveProject(null);
          }
        }

        return remaining;
      });
    } catch (err) {
      console.error('Failed to delete agent:', err);
      throw err;
    }
  }, [activeProject?.id]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Folder/Item CRUD Operations — each on the one agent's tree that holds it
  // ─────────────────────────────────────────────────────────────────────────────

  const createFolder = useCallback(async (name: string, agentId: string, parentId?: string | null): Promise<TreeItem> => {
    if (!activeProject) {
      throw new Error('No active agent');
    }
    const newFolder = await foldersApi.create(activeProject.id, {
      name,
      agent_id: agentId,
      parent_folder_id: parentId ?? undefined,
    });
    useAgentTreeStore.getState().setTree(agentId, (items) => addToTree(items, newFolder, parentId));
    return newFolder;
  }, [activeProject]);

  const moveItem = useCallback(async (itemId: string, newParentId: string | null): Promise<void> => {
    const found = locateItem(itemId);
    if (!found) throw new Error('Item not found');
    // Conversations have no folder; this guard keeps the routing honest.
    if (found.conversation) return;

    const store = useAgentTreeStore.getState();
    const original = store.trees[found.agentId] ?? [];
    store.setTree(found.agentId, (items) => addToTree(removeFromTree(items, itemId), found.item, newParentId));
    try {
      await treeItemsApi.move(itemId, newParentId, found.item.type);
    } catch (err) {
      console.error('Failed to move item:', err);
      store.setTree(found.agentId, () => original);
      throw err;
    }
  }, []);

  const renameItem = useCallback(async (itemId: string, newName: string): Promise<void> => {
    const found = locateItem(itemId);
    if (!found) throw new Error('Item not found');

    const store = useAgentTreeStore.getState();
    const set = found.conversation ? store.setConversations : store.setTree;
    const original = (found.conversation ? store.conversations : store.trees)[found.agentId] ?? [];
    set(found.agentId, (items) => renameInTree(items, itemId, newName));
    try {
      await treeItemsApi.rename(itemId, newName, found.item.type);
      // A page is a file: its pins and Recent row follow the pages listing.
      if (found.item.type !== 'folder' && found.item.type !== 'conversation') usePagesIndexStore.getState().refresh();
    } catch (err) {
      console.error('Failed to rename item:', err);
      set(found.agentId, () => original);
      throw err;
    }
  }, []);

  const deleteItem = useCallback(async (itemId: string): Promise<void> => {
    const found = locateItem(itemId);
    if (!found) throw new Error('Item not found');

    const store = useAgentTreeStore.getState();
    const set = found.conversation ? store.setConversations : store.setTree;
    const original = (found.conversation ? store.conversations : store.trees)[found.agentId] ?? [];
    set(found.agentId, (items) => removeFromTree(items, itemId));
    setRecent((prev) => (prev ? { ...prev, items: prev.items.filter((i) => i.id !== itemId) } : prev));
    try {
      await treeItemsApi.delete(itemId, found.item.type);
    } catch (err) {
      console.error('Failed to delete item:', err);
      set(found.agentId, () => original);
      throw err;
    }
  }, []);

  // Add an item before the server lists it (uploads, imports). Prepended:
  // the files listing is newest first.
  const addOptimisticItem = useCallback((item: TreeItem): void => {
    const agentId = item.agentId;
    if (!agentId) return;
    const store = useAgentTreeStore.getState();
    if (item.type === 'conversation') {
      if (!store.conversationLoad[agentId]) return;
      store.setConversations(agentId, (items) => (items.some((i) => i.id === item.id) ? items : [item, ...items]));
      return;
    }
    if (!store.treeLoad[agentId]) return;
    store.setTree(agentId, (items) => addToTree(items, item, item.parentId, true));
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        activeProject,
        setActiveProject: handleSetActiveProject,
        isLoading,
        recentItems: recent && recent.projectId === activeProject?.id ? recent.items : [],
        refreshRecent,
        createProject,
        updateProject,
        deleteProject,
        refreshProjects: fetchProjects,
        createFolder,
        moveItem,
        renameItem,
        deleteItem,
        addOptimisticItem,
        error,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject(): ExtendedProjectContextType {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}
